import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentResult() {
  const { pathname, search } = useLocation();
  const isCancel = pathname.includes("cancel");
  const sessionId = new URLSearchParams(search).get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current < 15) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, sessionId]);

  const content = {
    polling: {
      icon: <Loader2 size={30} className="text-[#D4AF37] animate-spin" />,
      title: "Confirming your payment…",
      body: "One moment while the cellar confirms your first quarterly payment.",
      testid: "payment-polling",
    },
    paid: {
      icon: <Check size={30} className="text-[#D4AF37]" />,
      title: "Welcome to Cellar Reserve.",
      body: "Your membership is active. Your first six-bottle allocation is being prepared — watch your inbox for pick-up party dates.",
      testid: "payment-success",
    },
    cancelled: {
      icon: <XCircle size={30} className="text-[#C86D51]" />,
      title: "Checkout cancelled.",
      body: "No charge was made. Your reserve allocation is still waiting whenever you're ready.",
      testid: "payment-cancelled",
    },
    timeout: {
      icon: <Loader2 size={30} className="text-[#D4AF37]" />,
      title: "Still confirming…",
      body: "Your payment is taking longer than usual to confirm. If you were charged, your membership will activate automatically — check back shortly or email admin@deltabreezewinery.com.",
      testid: "payment-timeout",
    },
  }[state];

  return (
    <div className="min-h-screen bg-[#0A0908] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid={content.testid}
        className="w-full max-w-md bg-[#141210] border border-[rgba(212,175,55,0.25)] p-10 text-center"
      >
        <div className="flex justify-center mb-6">{content.icon}</div>
        <p className="overline-label">Cellar Reserve</p>
        <h1 className="font-serif-display text-3xl font-light text-[#F4F1EA] mt-3">{content.title}</h1>
        <p className="text-sm text-[#A39E93] leading-relaxed mt-4">{content.body}</p>
        <Link
          to="/"
          data-testid="payment-back-home"
          className="inline-block mt-8 font-mono text-xs uppercase tracking-[0.25em] border border-[#D4AF37] text-[#D4AF37] px-8 py-4 hover:bg-[#D4AF37] hover:text-[#0A0908] transition-colors"
        >
          Back to the Estate
        </Link>
      </motion.div>
    </div>
  );
}
